// Modal create/edit câu hỏi — dùng cho cả nút "Thêm câu" và "Sửa" row.
// KHÔNG handle move-bo (đã có MoveBoDialog) và KHÔNG handle approve (ChoduyetActions).
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BO_ENUM, boLabel } from '../lib/bo.js';
import RichTextEditor from './RichTextEditor.jsx';
// M62 (2026-04-25): convert markdown (AI generate) → HTML khi mở modal sửa
// để TipTap editor hiển thị WYSIWYG đẹp thay vì markdown thô.
import { toHtmlForEditor } from '../lib/renderRichText.js';
// M135 (2026-05-10): Combobox chủ đề — gợi ý topics đã có trong bộ + cho gõ topic mới.
import TopicCombobox from './TopicCombobox.jsx';

export default function QuestionEditor({ open, mode, initial, defaultBo, onClose, onSave }) {
  // mode = 'create' | 'edit' | 'approve-edit'
  const [bo, setBo] = useState(initial?.bo || defaultBo || 'cho_duyet');
  const [topic, setTopic] = useState(initial?.topic || '');
  // M62: convert markdown → HTML cho editor WYSIWYG. Lần đầu mount + mỗi lần
  // mở modal lại với câu khác đều convert. Sau khi user gõ, state lưu HTML →
  // các lần convert tiếp toHtmlForEditor sẽ pass-through (HTML → HTML, idempotent).
  const [question, setQuestion] = useState(toHtmlForEditor(initial?.question));
  const [answer, setAnswer] = useState(toHtmlForEditor(initial?.answer));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBo(initial?.bo || defaultBo || 'cho_duyet');
    setTopic(initial?.topic || '');
    setQuestion(toHtmlForEditor(initial?.question));
    setAnswer(toHtmlForEditor(initial?.answer));
    setSaving(false);
  }, [open, initial, defaultBo]);

  if (!open) return null;

  const isEditMode = mode === 'edit' || mode === 'approve-edit';
  const title =
    mode === 'create' ? 'Thêm câu hỏi mới' :
    mode === 'approve-edit' ? 'Sửa + Duyệt → bộ "AI đã sửa"' :
    'Sửa câu hỏi';

  async function handleSave() {
    const q = question.trim();
    const a = answer.trim();
    if (!q) return toast.error('Thiếu câu hỏi');
    if (!a) return toast.error('Thiếu đáp án');
    if (q.length > 5000) return toast.error('Câu hỏi > 5000 ký tự');
    if (a.length > 20000) return toast.error('Đáp án > 20000 ký tự');

    setSaving(true);
    try {
      await onSave({
        bo,
        topic: topic.trim() || null,
        question: q,
        answer: a,
      });
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Lỗi lưu');
      setSaving(false);
      return;
    }
    setSaving(false);
  }

  return (
    // M136 (2026-05-10): Bỏ onClick={onClose} trên backdrop — admin gõ câu hỏi
    // dài rồi lỡ tay click ngoài form → mất hết. Giờ chỉ đóng qua nút × hoặc Huỷ.
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button className="text-2xl leading-none text-slate-400 hover:text-slate-700" onClick={onClose}>×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Bộ chỉ chọn ở mode create — sửa thì dùng Move-bo */}
          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bộ đích</label>
              <select value={bo} onChange={(e) => setBo(e.target.value)}
                      className="w-full border rounded px-3 py-2">
                {BO_ENUM.map((b) => <option key={b} value={b}>{boLabel(b)}</option>)}
              </select>
            </div>
          )}
          {isEditMode && (
            <div className="text-xs text-slate-500">
              Bộ hiện tại: <b>{boLabel(initial?.bo)}</b>
              {mode === 'edit' && ' (đổi bộ bằng nút "Chuyển bộ" ở danh sách)'}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Chủ đề (tuỳ chọn)</label>
            <TopicCombobox value={topic} onChange={setTopic} bo={bo} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Câu hỏi <span className="text-red-500">*</span>
              <span className="float-right text-xs text-slate-400">{question.length}/5000</span>
            </label>
            <RichTextEditor
              value={question}
              onChange={setQuestion}
              placeholder="Viết câu hỏi… (hỗ trợ định dạng, ảnh)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Đáp án <span className="text-red-500">*</span>
              <span className="float-right text-xs text-slate-400">{answer.length}/20000</span>
            </label>
            <RichTextEditor
              value={answer}
              onChange={setAnswer}
              placeholder="Viết đáp án chi tiết… (hỗ trợ định dạng, ảnh, list)"
            />
          </div>
        </div>

        <div className="border-t px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving}
                  className="px-4 py-2 border rounded hover:bg-slate-50">
            Huỷ
          </button>
          <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
            {saving ? 'Đang lưu…' : mode === 'approve-edit' ? 'Lưu + Duyệt' : 'Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
}
